import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import { deviationJobsQuery } from '@/queries/deviation.query';
import { layersQuery } from '@/queries/layer.query';
import { deviationTrackingAtom } from '@/atoms/deviation-progress.atom';

/**
 * Mounts once per project shell.
 *
 * Two responsibilities, deliberately decoupled:
 *
 * 1. **Toast updates** — driven by in-memory trackers populated by the
 *    deviation modal's onSuccess. Only this session's jobs get a toast,
 *    which is the desired behaviour (toast IDs are local to sonner).
 *
 * 2. **Badge count** — derived from the live job query, not the trackers,
 *    so reloading the page or starting a job in another tab still surfaces
 *    a badge if the server has anything in WAITING/PROCESSING.
 *
 * ## Polling
 *
 * We do NOT rely on TanStack Query's `refetchInterval` for the deviation /
 * layers queries here. Both query keys (`['deviationJobs', projectId]` and
 * `['layers', projectId]`) are also subscribed by other hooks on the same
 * page (`useLayers` for layers; `AnalysesPage` for deviationJobs). When
 * multiple observers share a query key, TanStack Query's interval
 * coordination is fragile — particularly when the other observers use the
 * callback form of `refetchInterval` that re-evaluates on every cycle.
 * Two earlier attempts to fix the "toast spins forever" bug by tweaking
 * `refetchInterval` here were defeated by the page-level `useLayers`
 * polling predicate returning false in its own callback.
 *
 * The reliable approach is an explicit `setInterval` driven by
 * `hasTrackers`, which calls `invalidateQueries` on both keys. The page's
 * own observers see the invalidation and refetch — we don't have to fight
 * for ownership of the polling cadence.
 */
export function useDeviationProgress(projectId: string) {
  const { t } = useTranslation('project');
  const [trackers, setTrackers] = useAtom(deviationTrackingAtom);
  const queryClient = useQueryClient();

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  // Read the deviation-jobs cache to fold server-side state into the polling
  // predicate. Lets the badge survive page reloads — if jobs are running
  // server-side, we should poll even without a local tracker. Note: this
  // is a one-shot cache read; it doesn't subscribe. We re-read it on every
  // render, which is fine because the cache is updated by the page's own
  // `deviationJobs` query subscriber (e.g. AnalysesPage when mounted).
  const cachedJobs = queryClient.getQueryData<{ deviationJobs: { status: string }[] }>([
    'deviationJobs',
    projectId,
  ]);
  const anyServerRunning =
    cachedJobs?.deviationJobs.some((j) => j.status === 'WAITING' || j.status === 'PROCESSING') ?? false;

  const shouldPoll = hasTrackers || anyServerRunning;

  // Read-only subscriptions — `refetch` returned here is what we drive the
  // polling with. Each `refetch()` call hits the network directly,
  // bypassing every observer-coordination question (`invalidateQueries`
  // requires an active observer to actually fire; sharing query keys with
  // the page's own hooks made that fragile across earlier attempts).
  const { data, refetch: refetchJobs } = useQuery({
    queryKey: ['deviationJobs', projectId],
    queryFn: () => execute(deviationJobsQuery, { projectId }),
    enabled: !!projectId,
  });

  const { data: layersData, refetch: refetchLayers } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: () => execute(layersQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  // Explicit polling. While `shouldPoll` is true, fire `refetch()` directly
  // every 2 s on both queries. `refetch` ignores `enabled` and any shared
  // observer's options, so a single mounted instance of this hook fully
  // owns the cadence — regardless of what `useLayers` / `AnalysesPage` do
  // with the same query keys. The console-visible network requests are
  // proof the cadence is alive; if you don't see them in DevTools while a
  // tracker exists, the bug is elsewhere (e.g. the modal failed to add a
  // tracker, or `projectId` is empty).
  useEffect(() => {
    if (!shouldPoll || !projectId) return;
    const tick = () => {
      void refetchJobs();
      void refetchLayers();
    };
    // Fire once immediately so we don't wait 2 s for the first tick — the
    // user just clicked Run and expects to see polling start now.
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [shouldPoll, projectId, refetchJobs, refetchLayers]);

  useEffect(() => {
    if (!hasTrackers) return;

    const jobs = data?.deviationJobs ?? [];
    const layers = layersData?.layers ?? [];

    setTrackers((prev) => {
      const next = [...prev];
      let changed = false;

      for (let i = next.length - 1; i >= 0; i--) {
        const tracker = next[i];
        if (tracker.projectId !== projectId) continue;

        // Failure path: short-circuit on a FAILED deviation job.
        const job = jobs.find((j) => j.batchId === tracker.batchId);
        if (job?.status === 'FAILED') {
          toast.error(
            t('deviationAnalysis.failedToast', { name: tracker.sourceLayerName }),
            { id: tracker.toastId, duration: 5000 },
          );
          next.splice(i, 1);
          changed = true;
          continue;
        }

        // Success path: the LAYER produced by postprocess has appeared.
        // Match by metadata.derivedFrom (set by postprocess from the
        // deviationContext). Disambiguate against pre-existing derived layers
        // by checking the snapshot the modal captured — the new layer is one
        // whose id isn't in `existingDerivedLayerIds`. We deliberately do NOT
        // compare createdAt to a client-captured `startedAt` because the two
        // clocks can disagree by several seconds, which previously caused the
        // toast to spin forever (client ahead) or resolve against a stale
        // layer (client behind).
        const existingIds = new Set(tracker.existingDerivedLayerIds);
        const newLayer = layers.find((l) => {
          const meta = (l.metadata ?? {}) as { derivedFrom?: string };
          if (meta.derivedFrom !== tracker.sourceLayerId) return false;
          return !existingIds.has(l.id);
        });
        if (newLayer) {
          toast.success(
            t('deviationAnalysis.completedToast', { name: tracker.sourceLayerName }),
            { id: tracker.toastId, duration: 5000 },
          );
          queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
          next.splice(i, 1);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [data, layersData, hasTrackers, projectId, queryClient, setTrackers, t]);

  // Badge derives from server state so it survives reloads / picks up jobs
  // started in another tab.
  const runningCount =
    data?.deviationJobs.filter((j) => j.status === 'WAITING' || j.status === 'PROCESSING').length ?? 0;

  return { runningCount };
}
